import React, { CSSProperties } from "react";

import { IPricingRecord } from "@services/QuoteBuilder/QuoteBuilderService";
import Label from "@components/inputs/text/Label";
import { PanelItem, IPanelItem } from "@components/panels/PanelItems";
import { CustomerPotentialDetail } from "./customerPotentialDetailReducer";
import { utcDateTimeExpressedInLocalTimeZone } from "@utils/misc/functions";
import { PlanType } from "@utils/quotePlanOptions";
import { Country } from "@utils/constants/localisation";
import { countrySpecificCurrency, countrySpecificDistanceUnit } from "@utils/misc/functions";
import { getLineItemByComponent } from "@utils/pricingRecordHelpers";


type Props = {
  customerPotentialDetailState?: CustomerPotentialDetail;
  pricingRecordState: IPricingRecord | null;
  foQuoting21Enabled?: boolean;
};


const style: CSSProperties = {
  display: "flex",
  flexDirection: "column",
};


// General helper function to get the details of a pricing line item by ID.
// Typically used for displaying the pricing plan name.
const getLineItemByLabelCode = (
  pricingRecord: IPricingRecord,
  lineItemLabelCode: string
) => {
  return pricingRecord?.pricing_record_line_items?.find(
    (item) => item.line_item_label_code === lineItemLabelCode
  );
};


// Helper function to convert year strings (e.g., "4y", "5y") to weeks
const convertTermLengthToWeeks = (
  termLengthInYears: number | undefined | null,
  termReduction: number | undefined | null = 0
): string => {
  if (!termLengthInYears) return "";

  // Converts the term length from years to weeks
  let convertedTermLength = termLengthInYears * 52;
  if (termReduction) {
    convertedTermLength = convertedTermLength - termReduction;
  }

  return convertedTermLength.toString();
};

export default function PlanSummaryComponent({
  customerPotentialDetailState,
  pricingRecordState,
  foQuoting21Enabled
}: Props) {
  const country = pricingRecordState?.country as Country;
  const currency = countrySpecificCurrency(country);
  const distanceUnit = countrySpecificDistanceUnit(country);
  const lossAndDamagePaymentLineItem = getLineItemByComponent(pricingRecordState?.pricing_record_line_items || [], 'lossanddamagepayment')
    || getLineItemByComponent(pricingRecordState?.pricing_record_line_items || [], 'lossanddamagecovervehicle')
  
  // The following component is used for displaying Plan & Vehicle details
  // in Quote Builder and Onboarding's Sales Quote Preview.
  // If any fields are different, make sure to conditionally render them based on plan type
  const planVehicleDetails: IPanelItem[] = [
    {
      header: "Plan Name",
      headerTextStyleCfg: { bold: true },
      // Plan names can be long; allow the value to wrap and right-align so it
      // doesn't crush the "Plan Name" header into an ellipsis.
      valueTextStyleCfg: { lineBreak: true },
      value:
        (pricingRecordState &&
          getLineItemByLabelCode(pricingRecordState, "A1")
            ?.chargebee_item_name) ||
        "",
    },
    {
      header: "Plan Type",
      headerTextStyleCfg: { bold: true },
      value:
        customerPotentialDetailState?.planType ||
        pricingRecordState?.zoho_potential.Plan,
    },
  ];

  if (pricingRecordState?.plan === PlanType.FLEXI_OWN) {
    planVehicleDetails.push(
      {
        header: "Contract Term (weeks)",
        headerTextStyleCfg: { bold: true },
        value: convertTermLengthToWeeks(
          pricingRecordState?.term_length_in_years,
          pricingRecordState?.term_reduction
        ),
      }
    )
  } 
  else {
    planVehicleDetails.push(
      {
        header: "Minimum Term (weeks)",
        headerTextStyleCfg: { bold: true },
        value: pricingRecordState?.minimum_period_weeks,
      }
    )
  }

  planVehicleDetails.push(
    {
      header:
        pricingRecordState?.plan === PlanType.FLEXI_OWN
          ? "Make / Model / Year"
          : "Make / Model",
      headerTextStyleCfg: { bold: true },
      value: pricingRecordState?.plan === PlanType.FLEXI_OWN
              ? `${pricingRecordState?.make} ${pricingRecordState?.model} ${pricingRecordState?.model_year}` 
              : `${pricingRecordState?.make} ${pricingRecordState?.model}`,
    },
    {
      header: `Weekly Distance Allowance (${distanceUnit})`,
      headerTextStyleCfg: { bold: true },
      value: foQuoting21Enabled ?
              (lossAndDamagePaymentLineItem?.included ? pricingRecordState?.weekly_distance_allowance : 'N/A') :
              pricingRecordState?.weekly_distance_allowance,
    },
    {
      header: `Excess ${distanceUnit} Charge (${currency})`,
      headerTextStyleCfg: { bold: true },
      value: foQuoting21Enabled ?
              (lossAndDamagePaymentLineItem?.included ? pricingRecordState?.excess_distance_cost?.toFixed(2) : 'N/A') :
              pricingRecordState?.excess_distance_cost?.toFixed(2),
    }
  )

  if (pricingRecordState?.vehicle_colour) {
    planVehicleDetails.push(
      {
        header: "Vehicle Colour",
        headerTextStyleCfg: { bold: true },
        value: pricingRecordState?.vehicle_colour,
      }
    )
  }

  planVehicleDetails.push(
    {
      header: "Estimated Delivery ETA",
      headerTextStyleCfg: { bold: true },
      value: pricingRecordState &&
        utcDateTimeExpressedInLocalTimeZone(
          pricingRecordState?.vehicle_delivery_eta
        )
      ,
    }
  )

  return (
    <>
      <Section
        childCmp={
          <>
            <Label
              text="Plan & Vehicle Details"
              styleCfg={{ bold: true, largeFont: true }}
            />
            <div style={style} data-testid={"planVehicleDetailsPanel"}>
              {planVehicleDetails.map((itemProps, index) => (
                <PanelItem
                  key={`planVehicleDetailsPanel-${index}`}
                  {...itemProps}
                />
              ))}
            </div>
          </>
        }
      />
    </>
  );
}


/**
 * Helper components
 */
const Section = ({ childCmp }: { childCmp: JSX.Element }) => (
  <div style={{ margin: "2.5vh 0vw" }}>{childCmp}</div>
);
